import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, UserPlus, Trash2, ShieldCheck, Send, Clock, X, Percent } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const GOLD = '#D4AF37';

const STATUS_LABEL = {
  pending: { text: 'Pendiente', color: 'amber', icon: Clock },
  accepted: { text: 'Aceptada', color: 'emerald', icon: ShieldCheck },
  rejected: { text: 'Rechazada', color: 'red', icon: X },
};

/**
 * Co-production tab for an event. Lets the OWNER invite other producers
 * with a revenue share %. Co-producers see read-only view (the owner switch
 * sits on the backend response `can_edit`).
 */
export default function CoProductionTab({ eventId, apiMode = 'producer' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [share, setShare] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const url = apiMode === 'admin'
        ? `/events/${eventId}/co-production`
        : `/co-production/events/${eventId}`;
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error('No se pudo cargar la co-producción');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [eventId, apiMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async () => {
    if (!query.trim()) {
      toast.error('Ingresá el nombre o @slug de la productora');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/co-production/events/${eventId}/invite`, {
        producer_query: query.trim(),
        share_percent: Number(share || 0),
      });
      toast.success('Invitación enviada');
      setQuery('');
      setShare('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar la invitación');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (coProducerId) => {
    if (!window.confirm('¿Quitar a esta productora de la co-producción?')) return;
    try {
      await api.delete(`/co-production/events/${eventId}/co-producers/${coProducerId}`);
      toast.success('Co-productora removida');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo remover');
    }
  };

  const updateShare = async (coProducerId, value) => {
    try {
      await api.put(`/co-production/events/${eventId}/co-producers/${coProducerId}/share`, {
        share_percent: Number(value || 0),
      });
      toast.success('% actualizado');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-500"><Loader2 className="mx-auto animate-spin" /></div>;
  }
  if (!data) return null;

  const canEdit = data.can_edit;
  const allParticipants = [data.primary, ...(data.co_producers || [])];
  const totalAcceptedShare = allParticipants
    .filter((p) => p.is_primary || p.status === 'accepted')
    .reduce((acc, p) => acc + (Number(p.share_percent) || 0), 0);

  return (
    <div className="space-y-6" data-testid="co-production-tab">
      <div>
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <UserPlus size={16} style={{ color: GOLD }} /> Co-producción del evento
        </h3>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Invitá a otra productora a co-producir este evento. Cuando acepte, va a tener
          acceso completo (entradas, órdenes, lineup) y compartir tu split de ingresos.
        </p>
      </div>

      {/* Invite form — only for owner */}
      {canEdit && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-5"
          data-testid="co-production-invite-card"
        >
          <p className="text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-3">Invitar productora</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3 items-end">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Productora</label>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Slug o nombre (ej. productora-demo)"
                className="bg-zinc-950 border-zinc-800"
                data-testid="co-production-invite-query"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-400 mb-1">% que se lleva</label>
              <div className="relative">
                <Input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={share}
                  onChange={(e) => setShare(e.target.value)}
                  placeholder="30"
                  className="bg-zinc-950 border-zinc-800 pr-8"
                  data-testid="co-production-invite-share"
                />
                <Percent size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              </div>
            </div>
            <Button
              onClick={invite}
              disabled={submitting}
              className="bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-bold"
              data-testid="co-production-invite-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
              Invitar
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-3">
            La otra productora va a recibir la invitación en su panel y tendrá que aceptar para sumarse.
          </p>
        </motion.div>
      )}

      {/* Participants list */}
      <div className="space-y-3" data-testid="co-production-participants">
        <p className="text-xs uppercase tracking-wider text-zinc-400 font-bold">
          Reparto actual <span className="text-zinc-600">· Suma aceptada {totalAcceptedShare.toFixed(1)}%</span>
        </p>

        {allParticipants.map((p) => (
          <ParticipantRow
            key={p.producer_id}
            participant={p}
            canEdit={canEdit && !p.is_primary}
            onRemove={() => remove(p.producer_id)}
            onShareChange={(v) => updateShare(p.producer_id, v)}
          />
        ))}
      </div>

      {!canEdit && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4 text-xs text-zinc-400">
          {apiMode === 'admin'
            ? 'Vista de administrador. Mostramos la co-producción tal como la ven las productoras participantes; los cambios al reparto los realiza la productora principal.'
            : 'Estás viendo esta co-producción como invitado. Tenés acceso completo al evento pero la gestión del reparto la maneja la productora principal.'}
        </div>
      )}
    </div>
  );
}

function ParticipantRow({ participant, canEdit, onRemove, onShareChange }) {
  const isPrimary = participant.is_primary;
  const status = participant.status || (isPrimary ? 'accepted' : 'pending');
  const cfg = STATUS_LABEL[status] || STATUS_LABEL.pending;
  const Icon = cfg.icon;
  const colorMap = {
    amber: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    red: 'border-red-500/40 bg-red-500/10 text-red-300',
  };

  const [localShare, setLocalShare] = useState(participant.share_percent ?? 0);
  useEffect(() => { setLocalShare(participant.share_percent ?? 0); }, [participant.share_percent]);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-4">
      {participant.logo ? (
        <img src={participant.logo} alt={participant.name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center font-bold text-zinc-300">
          {(participant.name || '?').charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-white truncate">{participant.name || '—'}</p>
          {isPrimary ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[9px] font-extrabold uppercase tracking-wider">
              <ShieldCheck size={9} /> Dueña
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${colorMap[cfg.color]}`}>
              <Icon size={10} /> {cfg.text}
            </span>
          )}
        </div>
        {participant.slug && (
          <p className="text-[11px] text-zinc-500 truncate">@{participant.slug}</p>
        )}
      </div>

      {/* Share editor / readout */}
      <div className="flex items-center gap-2">
        {canEdit ? (
          <div className="relative w-24">
            <Input
              type="number"
              step="1"
              min="0"
              max="100"
              value={localShare}
              onChange={(e) => setLocalShare(e.target.value)}
              onBlur={() => Number(localShare) !== Number(participant.share_percent || 0) && onShareChange(localShare)}
              className="bg-zinc-950 border-zinc-800 pr-7 text-right h-9"
              data-testid={`co-production-share-${participant.producer_id}`}
            />
            <Percent size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          </div>
        ) : (
          <div className="text-right">
            <p
              className="text-lg font-black"
              style={{ color: isPrimary ? GOLD : '#fff', fontFamily: "'Unbounded', sans-serif" }}
            >
              {Number(participant.share_percent || 0).toFixed(1)}%
            </p>
            <p className="text-[9px] uppercase tracking-wider text-zinc-500">share</p>
          </div>
        )}
        {canEdit && !isPrimary && (
          <Button
            size="sm"
            variant="outline"
            className="border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9"
            onClick={onRemove}
            data-testid={`co-production-remove-${participant.producer_id}`}
          >
            <Trash2 size={13} />
          </Button>
        )}
      </div>
    </div>
  );
}
